import { createSelector } from '@reduxjs/toolkit';
import { EntityName } from '@/types/editableField';
import { RootState } from '@/store/types/RootState';
import { EdsValidationMessageType } from '@/features/eds/enums/EdsValidationMessageType';

export const selectEditModeState = (state: RootState) => state.editMode.state;

export const selectEditModeStatus = (state: RootState) => state.editMode.status;

export const selectIsInEditMode = (state: RootState) => state.editMode.isInEditMode;

export const selectHasEditedFields = (state: RootState) => Boolean(state.editMode?.editedFields?.length);

export const selectEditedField = ({
  entityId,
  entityName,
  entityField,
}: {
  entityId?: number;
  entityName?: EntityName;
  entityField?: string;
}) =>
  createSelector([(state: RootState) => state.editMode.editedFields], (editedFields) => {
    if (entityId && entityName && entityField) {
      return editedFields.find(
        (field) => field.entityId === entityId && field.entityField === entityField && field.entityName === entityName,
      );
    }
  });

export const selectClaimSetUpdateOptions = (state: RootState) => state.editMode.claimSetUpdateOptions;
export const selectClaimSetClaimUpdateOptions = (state: RootState) => state.editMode.claimSetClaimUpdateOptions;
export const selectClaimSetTaskUpdateOptions = (state: RootState) => state.editMode.claimSetTaskUpdateOptions;
export const selectClaimantUpdateOptions = (state: RootState) => state.editMode.claimantUpdateOptions;
export const selectDocumentDeliveryPreferencesUpdateOptions = (state: RootState) =>
  state.editMode.documentDeliveryPreferencesUpdateOptions;
export const selectGenderUpdateOptions = (state: RootState) => state.editMode.genderUpdateOptions;
export const selectContactPreferenceUpdateOptions = (state: RootState) => state.editMode.contactPreferenceUpdateOptions;

export const selectReportingUserListData = (state: RootState) => state.editMode.reportingUserListData;
export const selectReportingUserListDataDataList = (state: RootState) => state.editMode.reportingUserListData?.dataList;
export const selectReportingUserListPagination = (state: RootState) => state.editMode.reportingUserListPagination;

export const selectEditModeDataRequestsStates = (state: RootState) => state.editMode.dataRequestsStates;

export const selectStateListData = (state: RootState) => state.editMode.stateListData;
export const selectStateListPagination = (state: RootState) => state.editMode.stateListPagination;
export const selectPolicyUpdateOptions = (state: RootState) => state.editMode.policyUpdateOptions;

export const selectCauseOfIncidentListData = (state: RootState) => state.editMode.causeOfIncidentListData;
export const selectCauseOfIncidentListPagination = (state: RootState) => state.editMode.causeOfIncidentListPagination;

export const selectProfileUpdateOptions = (state: RootState) => state.editMode.profileUpdateOptions;

export const selectNormalizedValidationResultList = (state: RootState) => state.editMode.normalizedValidationResultList;
export const selectBlocking = (state: RootState) => state.editMode.blocking;

const DEFAULT_EMPTY_ARRAY = [];
export const selectFieldValidationResult = createSelector(
  [selectNormalizedValidationResultList, (_state, props) => props],
  (normalizedValidationResultList, { entityId, entityName, entityField }) => {
    if (entityId && entityName && entityField) {
      return normalizedValidationResultList?.filter(
        (field) =>
          field.entityId === entityId &&
          field.entityField.toLowerCase() === entityField.toLowerCase() &&
          field.entityName === entityName,
      );
    }

    return DEFAULT_EMPTY_ARRAY;
  },
);

export const selectShouldCloseEditModeOnError = (state) => state.editMode.shouldCloseEditModeOnError;

export const selectShouldDisableEditModeSave = (state: RootState) =>
  state.editMode.isValidationInProgress ||
  state.editMode.normalizedValidationResultList?.some(
    (validationResult) => validationResult.messageType === EdsValidationMessageType.Error,
  );

export const selectAssigneeById = (id?: string | null) => (state: RootState) =>
  id ? state.editMode.assigneeByPersonId[id]?.data : null;
export const selectAllAssignees = (state: RootState) => state.editMode.assigneeByPersonId;

export const selectAssignmentListData = (state: RootState) => state.editMode.assignmentListData;

export const selectAssignmentListPagination = (state: RootState) => state.editMode.assignmentListPagination;

export const selectAssignmentListSearchExpression = (state: RootState) =>
  state.editMode.assignmentListPagination.searchExpression;

export const selectAssigneeListDataByQueueId = (queueId: string | number) => (state: RootState) =>
  state.editMode.assigneeListDataByQueueId[String(queueId)] || [];
